import { useMutation, useQuery } from "@tanstack/react-query"
import { FormSkeleton } from "components/new-stuff/ui/Skeleton"
import { Form, InputNumber, Switch } from "antd"
import { CustomInput, CustomSelect } from "components/new-stuff/form"
import FormSectionWrapper from "components/new-stuff/form/FormSectionWrapper"
import PrevNextButton from "components/new-stuff/form/PrevNextButton"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import { useNavigate, useSearchParams } from "react-router-dom"
import { ewaAxiosInstance } from "utils/request"
import {
  CreatePolicyPayload,
  ICreatePolicyResponse,
  IGetEWAPolicy,
} from "../interface/policies"
import { useAppNotificationStore } from "stores"
import { IEmployeesData } from "../interface/employees"

const EditPolicy = () => {
  const [searchParams] = useSearchParams()
  const { toast } = useAppNotificationStore()

  const [form] = Form.useForm()
  const navigate = useNavigate()
  const employees = Form.useWatch("employees", form)
  const policyId = searchParams.get("x")
  const { isLoading: isLoadingPolicy, data: policyInfo } =
    useQuery<IGetEWAPolicy>({
      queryKey: [`${policyId}-policy`],
      queryFn: async () => {
        const res = await ewaAxiosInstance.get(`/v1/group-policies/${policyId}`)
        return res.data
      },
      enabled: !!policyId,
      refetchOnWindowFocus: false,
      cacheTime: 24 * 60 * 60 * 1000,
      onSuccess(data) {
        form.setFieldsValue({
          name: data?.data?.name,
          limit: data?.data?.withdrawalPercent,
          approval: data?.data?.approvalRequired,
          employees: data?.data?.employees,
        })
      },
    })
  const {
    isLoading: isemployeesLoading,
    data: employeesData,
    refetch,
  } = useQuery<IEmployeesData>({
    queryKey: [`ewa-employees`],
    queryFn: async () => {
      const res = await ewaAxiosInstance.get(`/v1/employees?limit=0`)
      return res.data
    },

    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })

  const { mutate: editPolicy, isLoading } = useMutation({
    mutationFn: async (data: CreatePolicyPayload) => {
      const res = await ewaAxiosInstance.put(
        `/v1/group-policies/${policyId}`,
        data
      )
      return res.data
    },
    onSuccess(data: ICreatePolicyResponse) {
      navigate(-1)
      form.resetFields()
      data?.message && toast.success(data?.message)
    },
    onError(error: any) {
      toast.error(error?.response?.data?.message)
    },
  })

  const onFinish = (values: any) => {
    const { name, limit, approval } = values

    const body = {
      name,
      approvalRequired: approval,
      withdrawalPercent: limit,
      employeeIds: employees || [],
    }
    editPolicy(body)
  }

  return (
    <DashboardWrapper>
      {isLoadingPolicy ? (
        <FormSkeleton fields={6} />
      ) : (
        <FormSectionWrapper
          title={`Edit EWA Policy`}
          subtitle="Set different access levels for specific employee or groups based on their role, performance, or other factors"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
          isActive={true}
          footerBtns={
            <PrevNextButton
              prevBtnTitle="Cancel"
              nextBtnTitle="Update Policy"
              submitting={isLoading}
              onClickPrev={() => {
                navigate("/dashboard/ewa/policy-management")
              }}
            />
          }
        >
          <div className="flex flex-col px-4 md:px-6 xl:px-[60px] pb-4">
            <div className="f">
              <CustomInput
                label={"Policy Name "}
                name="name"
                required
              />

              <Form.Item
                label={
                  <span className="font-circular text-padeBlack text-sm">
                    Withdrawal Limit (%)
                  </span>
                }
                name="limit"
                className=" "
                rules={[
                  {
                    required: true,
                    message: "Please enter this field",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value) {
                        const amount = parseFloat(value)
                        if (amount > 100) {
                          return Promise.reject(
                            "Amount cannot be more than 100"
                          )
                        } else {
                          return Promise.resolve()
                        }
                      } else {
                        return Promise.resolve()
                      }
                    },
                  }),
                ]}
              >
                <InputNumber<number>
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    value?.replace(/,/g, "") as unknown as number
                  }
                  className="font-circular text-neutral400 w-full"
                  placeholder="e.g., 50%, 70%, or 100%"
                  suffix="%"
                />
              </Form.Item>

              <div className=" relative">
                <div className="flex gap-4 justify-end absolute z-10  top-1 right-0">
                  <button
                    type="button"
                    className="cursor-pointer text-blue-800"
                    onClick={() => {
                      const allpersons = employeesData?.data?.docs?.map(
                        (person) => person._id
                      )
                      // )
                      form.setFieldValue("employees", allpersons)
                    }}
                  >
                    Add All
                  </button>
                  <button
                    type="button"
                    className="cursor-pointer text-blue-800"
                    onClick={() => {
                      form.setFieldValue("employees", undefined)
                    }}
                  >
                    Remove All
                  </button>
                </div>

                <CustomSelect
                  showSearch
                  mode="multiple"
                  label={
                    <span className="font-avenir text-neutral300 text-sm">
                      Select Employees{" "}
                    </span>
                  }
                  name="employees"
                  required
                  options={
                    employeesData &&
                    employeesData?.data?.docs?.map((option) => ({
                      label: `${option?.firstName} ${option?.lastName} `,
                      value: option._id,
                    }))
                  }
                  isLoading={isemployeesLoading}
                  placeholder="Please Select"
                />
              </div>
              <div className=" flex items-center gap-2 ">
                <p className="text-padeBlack text-sm font-circular ">
                  Approval Requirement{" "}
                </p>
                <Form.Item
                  name="approval"
                  rootClassName="!mb-0"
                >
                  <Switch className="bg-[#ededed]" />
                </Form.Item>
              </div>
            </div>
          </div>
        </FormSectionWrapper>
      )}
    </DashboardWrapper>
  )
}

export default EditPolicy
